'use client';

import { createContext, useContext, useEffect, useCallback } from 'react';
import type { Locale } from '@/lib/i18n/config';
import { getDictionary } from '@/lib/i18n/dictionaries';
import { i18n } from '@/lib/i18n/config';
import { clog } from '@/lib/logger/client';

type Dictionary = Awaited<ReturnType<typeof getDictionary>>;

interface TranslationContextType {
  preloadDictionary: (locale: Locale) => Promise<void>;
  getPreloadedDictionary: (locale: Locale) => Dictionary | null;
}

const TranslationContext = createContext<TranslationContextType | undefined>(
  undefined,
);

// Global cache shared with useTranslations hook
export const dictionaryCache = new Map<Locale, Dictionary>();

// Track ongoing preloads to avoid duplicate requests
const preloadPromises = new Map<Locale, Promise<Dictionary>>();

export function TranslationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const preloadDictionary = useCallback(async (locale: Locale) => {
    // In development, always reload to pick up translation changes
    // In production, skip if already cached
    if (process.env.NODE_ENV === 'production' && dictionaryCache.has(locale)) {
      return;
    }

    // If already preloading, return the existing promise
    const existingPromise = preloadPromises.get(locale);
    if (existingPromise) {
      return existingPromise.then(() => undefined);
    }

    // Start preloading
    const promise = (async () => {
      try {
        const dict = await getDictionary(locale);
        dictionaryCache.set(locale, dict);
        preloadPromises.delete(locale);
        return dict;
      } catch (error) {
        preloadPromises.delete(locale);
        clog.error(`Failed to preload dictionary for locale ${locale}:`, error);
        throw error;
      }
    })();

    preloadPromises.set(locale, promise);
    return promise.then(() => undefined);
  }, []);

  const getPreloadedDictionary = useCallback((locale: Locale) => {
    return dictionaryCache.get(locale) ?? null;
  }, []);

  // Preload default locale immediately on mount
  useEffect(() => {
    preloadDictionary(i18n.defaultLocale);
  }, [preloadDictionary]);

  return (
    <TranslationContext.Provider
      value={{ preloadDictionary, getPreloadedDictionary }}
    >
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslationContext() {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error(
      'useTranslationContext must be used within a TranslationProvider',
    );
  }
  return context;
}
